import { ArrowRight, X } from "lucide-react";
import { useEffect, useRef } from "react";

/**
 * Item interface for upcoming changes
 */
type Item = { 
  /** Item title */
  title: string; 
  /** Item description */
  description: string;
};

/**
 * Props for the UpcomingChangesModal component
 */
type Props = {
  /** Whether modal is open */
  open: boolean;
  /** Callback fired when modal is closed */
  onClose: () => void;
  /** Modal title */
  title: string;
  /** Introduction text */
  intro: string;
  /** Array of upcoming change items */
  items: Item[];
};

/**
 * UpcomingChangesModal component displays a modal with upcoming changes information.
 * Shows title, introduction, and a list of change items. Closes on Escape key or
 * backdrop click. Focuses dialog on open for accessibility.
 * 
 * @component
 * @param {Props} props - Component props
 * @param {boolean} props.open - Whether modal is open
 * @param {() => void} props.onClose - Close callback
 * @param {string} props.title - Modal title
 * @param {string} props.intro - Introduction text
 * @param {Item[]} props.items - Array of change items
 * @returns {JSX.Element | null} Modal with upcoming changes or null if not open
 * 
 * @example
 * ```tsx
 * <UpcomingChangesModal
 *   open={isOpen}
 *   onClose={handleClose}
 *   title="Upcoming Changes"
 *   intro="Here are the upcoming features"
 *   items={changes}
 * />
 * ```
 */
export default function UpcomingChangesModal({
  open,
  onClose,
  title,
  intro,
  items,
}: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) dialogRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[102] flex items-start justify-start font-normal p-0 sm:p-4 md:items-center md:justify-center"
      aria-modal="true"
      role="dialog"
      aria-labelledby="upcoming-changes-title"
    >
      <div
        className="absolute inset-0 backdrop-blur-sm bg-[rgba(134,175,199,0.7)]"
        aria-hidden="true"
      />

      <div
        ref={dialogRef}
        tabIndex={-1}
        className="relative w-full h-full sm:h-auto sm:max-w-4xl bg-white outline-none text-primary-500 rounded-none sm:rounded-md overflow-y-auto flex flex-col"
      >
        <div className="bg-gradient-to-b from-slate-50/60 to-white rounded-none sm:rounded-md flex flex-col h-full">
          <div className="flex flex-col flex-shrink-0">
            <div className="flex items-center justify-between px-6 sm:px-6 pt-6">
              <div
                id="upcoming-changes-title"
                className="text-lg sm:text-3xl font-medium"
              >
                {title}
              </div>
              <button
                onClick={onClose}
                aria-label="Close"
                className="rounded-lg hover:bg-gray-100 active:bg-gray-200"
              >
                <X className="h-5 w-5 text-secondary" />
              </button>
            </div>
            <p className="px-6 leading-relaxed font-secondary text-[#373A3C] font-normal pb-2">
              {intro}
            </p>
          </div>

          <div className="px-6 py-3 py-6 flex-1 overflow-y-auto">
            <ul className="space-y-4 sm:space-y-6">
              {items.map((it, idx) => (
                <li key={idx} className="flex items-start flex-col">
                  <div className="flex font-primary">
                    <div
                      aria-hidden
                      className="select-none pt-1 pr-2"
                    >
                      <ArrowRight className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="font-primary font-medium text-[#005587]">
                        {it.title}
                      </div>
                      <div className="font-secondary font-medium text-sm text-[#71717B]">
                        {it.description}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex justify-end pb-6 px-6 flex-shrink-0">
            <button
              onClick={onClose}
              className="w-full sm:w-auto rounded-lg bg-primary px-4 py-3 text-white hover:opacity-90 active:opacity-80 bg-primary-500 font-primary text-sm sm:text-base"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}